import { useEffect, useState } from "react";
import { api, type AgentCardsState } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { resolveAuthenticated } from "@/lib/devConfig";

/**
 * Module-level stale-while-revalidate cache for list_agent_cards — mirrors
 * useDrawGacha's drawCache pattern (keyed per user, listener broadcast) so
 * CollectionPage, /profile's Collection tab and Home's CollectionShowcase
 * share one snapshot instead of each independently refetching and flashing
 * skeletons. Every mount still silently re-fetches (supply/ownership must
 * stay fresh); the cache only removes the loading flash and dedupes
 * concurrent calls.
 */
let cardsCache: { key: string; value: AgentCardsState } | null = null;
let cardsInflight: { key: string; promise: Promise<AgentCardsState> } | null = null;
const cardsListeners = new Set<() => void>();

function notifyCardsListeners() {
  for (const notify of cardsListeners) notify();
}

/**
 * Seed the cache from a server response that already carries the full
 * post-mutation snapshot (e.g. PullGachaResult.state) — keeps a Draw →
 * Collection navigation from briefly rendering the pre-pull cache.
 */
export function seedAgentCardsCache(key: string, value: AgentCardsState) {
  cardsCache = { key, value };
  notifyCardsListeners();
}

function fetchAgentCards(cacheKey: string): Promise<AgentCardsState> {
  if (!cardsInflight || cardsInflight.key !== cacheKey) {
    const promise = api.listAgentCards().finally(() => {
      // Only clear if we're still the tracked inflight request — a newer
      // call for a different key may have already replaced us.
      if (cardsInflight?.promise === promise) cardsInflight = null;
    });
    cardsInflight = { key: cacheKey, promise };
  }
  return cardsInflight.promise;
}

/**
 * Collection fetch — extracted from CollectionPage so /profile and Home's
 * showcase can mount the same data source (mirrors useDrawGacha's useDrawData
 * shape). Renders the cached snapshot immediately when one exists for the
 * current user, then swaps in the fresh response.
 */
export function useCollectionData() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const authenticated = resolveAuthenticated(session?.user);
  const cacheKey = session?.user?.id ?? "dev-bypass";

  // Re-render trigger; the rendered value is DERIVED from the module cache
  // keyed by the CURRENT user, so a user switch never shows stale numbers.
  const [, setVersion] = useState(0);
  const [loading, setLoading] = useState(() => authenticated && cardsCache?.key !== cacheKey);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const notify = () => setVersion((v) => v + 1);
    cardsListeners.add(notify);
    return () => {
      cardsListeners.delete(notify);
    };
  }, []);

  useEffect(() => {
    if (sessionPending) return;
    if (!authenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Loading UI only when there's nothing cached to render — SWR otherwise.
    setLoading(cardsCache?.key !== cacheKey);
    setError(null);
    fetchAgentCards(cacheKey)
      .then((next) => {
        cardsCache = { key: cacheKey, value: next };
        notifyCardsListeners();
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load your collection");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authenticated, sessionPending, cacheKey, reloadToken]);

  const state = authenticated && cardsCache?.key === cacheKey ? cardsCache.value : null;

  return {
    authenticated,
    sessionPending,
    state,
    loading: sessionPending || loading,
    error,
    reload: () => setReloadToken((token) => token + 1),
  };
}
